// react
import { useRef, useState } from 'react'

// external libs
import { Box, makeStyles, Paper, Typography } from '@material-ui/core'
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays'
import { useForm } from 'react-final-form'

// components
import MyButton from '../../../../control/button/button'
import MyTextfield from '../../../../control/textfield/textfield'
import DocPreview from './DocPreview'

// hooks
import useAlertMessageAction from '../../../../stores/appAlertMessage/appAlertMessageAction'
import useTempFile from '../../../../hooks/useTempFile'
import useTranslate from '../../../../i18n/useTranslate'

export interface AdditionalDocFieldProps {
    no?: number
    name: string
    isEditable?: boolean
    disabled?: boolean
    onDelete?: () => void
}

const ACCEPT = '.png,.jpeg,.tiff,.pdf,.doc,.docx'

const AdditionalDocField = (props: AdditionalDocFieldProps) => {
    const { no, name, isEditable, disabled, onDelete } = props
    const form = useForm()
    const inputRef = useRef<HTMLInputElement | null>(null)
    const tempFile = useTempFile({ accept: ACCEPT })
    const translate = useTranslate()
    const alertMessageAction = useAlertMessageAction()
    const classes = useStyles()

    const [isUploading, setIsUploading] = useState<boolean>(false)

    const handleUploadFile = async (
        e: React.ChangeEvent<HTMLInputElement>,
        fields: FieldArrayRenderProps<any, HTMLElement>['fields'],
    ) => {
        try {
            setIsUploading(true)
            const files = e.target.files

            if (!files || files.length === 0) {
                return
            }

            const file = files[0]

            const isDuplicate = fields.value.some((e) => e.filename === file.name)
            if (isDuplicate) {
                alertMessageAction.openAlertMessage({
                    title: translate.get('ALERT.TITLE.WARNING'),
                    message: translate.get('ALERT.CONTENT.UPLOAD_FILE_DUPLICATED'),
                })
                return
            }

            const isValid = tempFile.validate(file)
            if (!isValid) {
                return
            }

            const fileUploaded = await tempFile.upload(file)
            if (!fileUploaded) {
                return
            }

            const fileInfo = { ...fileUploaded, date: new Date().toISOString() }
            fields.push(fileInfo)
        } catch (error) {
            console.error('Has an error while adding file to fileInfo: ', error)
        } finally {
            if (inputRef.current) {
                inputRef.current.value = ''
            }
            setIsUploading(false)
        }
    }

    return (
        <Box display="flex" flexDirection="column" width="100%" className={classes.additionalDocField}>
            <FieldArray name={`${name}.fileInfo`}>
                {({ fields }) => {
                    return (
                        <>
                            <input
                                ref={inputRef}
                                type="file"
                                accept={ACCEPT}
                                hidden
                                onChange={(e) => handleUploadFile(e, fields)}
                            />
                            <Box width="100%" display="flex" justifyContent="space-between" alignItems="center">
                                <Typography> {no}. เอกสารเพิ่มเติม </Typography>
                                <Box display="flex" style={{ gap: 8 }}>
                                    <MyButton
                                        variant="contained"
                                        color="primary"
                                        size={'small'}
                                        className={classes.actionButton}
                                        disabled={isUploading || disabled}
                                        onClick={() => inputRef.current?.click()}
                                    >
                                        {translate.get('BUTTON.UPLOAD')}
                                    </MyButton>
                                    <MyButton
                                        variant="contained"
                                        color="secondary"
                                        size={'small'}
                                        className={classes.actionButton}
                                        disabled={disabled}
                                        onClick={onDelete}
                                    >
                                        {translate.get('BUTTON.DELETE')}
                                    </MyButton>
                                </Box>
                            </Box>
                            <MyTextfield
                                name={`${name}.topicName`}
                                maxLength={250}
                                materialInputProps={{
                                    placeholder: 'กรอกชื่อเอกสาร',
                                    size: 'small',
                                    disabled,
                                }}
                            />

                            <Paper variant="outlined" className={classes.documentWrapper}>
                                {fields.map((fieldName, index) => (
                                    <DocPreview
                                        key={fieldName}
                                        fileInfo={fields.value[index]}
                                        editable={isEditable}
                                        disabled={disabled}
                                        onChange={(e) => form.change(fieldName, e)}
                                        onDelete={() => fields.remove(index)}
                                    />
                                ))}

                                {fields.length === 0 ? (
                                    <Typography variant={'body2'}>{translate.get('LABEL.NO_FILE_UPLOAD')}</Typography>
                                ) : null}
                            </Paper>
                        </>
                    )
                }}
            </FieldArray>
        </Box>
    )
}

const useStyles = makeStyles(() => ({
    additionalDocField: {
        '&:not(:last-child)': {
            paddingBottom: 32,
            marginBottom: 32,
            borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
        },
    },
    documentWrapper: {
        marginTop: -32,
        zIndex: 1,
        padding: '12px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    },

    actionButton: {
        minWidth: '64px !important',
        flexShrink: 0,
    },
}))

export default AdditionalDocField
